import { useId, useMemo } from 'react';
import { PersonDot } from 'components/crm/ui/AssignMenu';
import Label from 'components/crm/ui/Label';
import RichSelect from 'components/crm/ui/RichSelect';
import useText from 'hooks/useText';

const fullName = (person) =>
  `${person.firstname ?? ''} ${person.lastname ?? ''}`.trim();

// RichSelect draws an option's icon as a component: each person's dot is one.
const dotOf = (person) => {
  const Dot = ({ size }) => (
    <PersonDot
      firstname={person.firstname}
      lastname={person.lastname}
      size={size}
    />
  );
  return Dot;
};

/**
 * « Activité attribuée à »: the signed-in person first, as « {prénom} (moi) »,
 * then the colleagues in the api's order, each with their dot. A value missing
 * from the team (nobody, someone who left) shows as nothing chosen rather than
 * as oneself. `readOnly` (a received task, without canAssign): the dot and
 * the name, nothing to open.
 */
const TaskAssigneeField = ({
  id = 'task-assignee',
  team,
  meId,
  value,
  onChange,
  readOnly = false,
  label = null,
}) => {
  const { text } = useText('taskForm');
  const labelId = useId();
  const options = useMemo(() => {
    const me = team.find((person) => person.id === meId);
    const ordered = me ? [me, ...team.filter((person) => person !== me)] : team;
    return ordered.map((person) => ({
      value: person.id,
      label:
        person === me
          ? text('me', { name: person.firstname ?? '' })
          : fullName(person),
      icon: dotOf(person),
    }));
  }, [team, meId, text]);

  if (readOnly) {
    const current = options.find((option) => option.value === value);
    return (
      <div className="flex flex-col gap-2">
        <span id={labelId}>
          <Label>{text('assigneeLabel')}</Label>
        </span>
        <div
          id={id}
          role="group"
          aria-labelledby={labelId}
          className="flex items-center gap-2 rounded-lg border border-line bg-surfaceMuted p-3 text-sm font-medium text-ink"
        >
          {current ? (
            <>
              <current.icon size={18} />
              <span className="min-w-0 flex-1 truncate">{current.label}</span>
            </>
          ) : (
            <span className="text-faint">{text('assigneeNobody')}</span>
          )}
        </div>
      </div>
    );
  }
  return (
    <RichSelect
      id={id}
      label={label ?? text('assigneeLabel')}
      dense
      searchable
      value={value}
      options={options}
      onChange={onChange}
    />
  );
};

export default TaskAssigneeField;
